import React from 'react';
import { Link } from 'react-router-dom';
import PublicPageNav from '../components/PublicPageNav';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#2D2D2D', color: '#e5e5e5' }} data-testid="privacy-policy-page">
      <PublicPageNav testIdPrefix="privacy" />

      <main className="max-w-3xl mx-auto px-6 py-12">
        <p className="text-xs uppercase tracking-[0.3em] mb-3" style={{ color: '#C9A962' }}>Legal</p>
        <h1 className="text-4xl sm:text-5xl font-bold mb-3 text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif', textTransform: 'uppercase' }}>
          Privacy <span style={{ color: '#C9A962' }}>Policy</span>
        </h1>
        <p className="text-sm mb-10" style={{ color: '#a0a0a0' }} data-testid="privacy-last-updated">Last updated: September 30, 2026</p>

        <Section title="1. Who We Are">
          <p>
            Noble Empire Cleaning Services, LLC ("Noble Empire", "NECS", "we", "our", or "us") is a Dallas-area valet trash, commercial cleaning, and property maintenance company headquartered at 2450 Lakeside Pkwy, Ste 150, Flower Mound, TX 75022. We operate two mobile applications — the <strong>NECS Resident App</strong> (for tenants and residents) and the <strong>NECS Staff App</strong> (for our drivers, supervisors, and crew leaders) — along with the noble­empirecs.com website (collectively, the "Services").
          </p>
          <p>
            This Privacy Policy describes what personal information we collect, how we use it, and your rights. By using the Services, you agree to the practices described below.
          </p>
        </Section>

        <Section title="2. Information We Collect">
          <div data-testid="privacy-data-safety-summary" className="rounded-lg border border-[#C9A962]/40 bg-black/30 p-4 mb-4">
            <h3 className="text-[#FFD60A] font-bold mb-2 text-sm uppercase tracking-widest">
              Data Safety Summary
            </h3>
            <p className="text-xs mb-2 text-zinc-300">
              For Google Play and Apple App Store transparency, here is the complete
              list of data categories NECS collects and why:
            </p>
            <ul className="list-disc pl-6 space-y-1.5 text-sm">
              <li data-testid="privacy-ds-personal">
                <strong>Personal Info</strong> &mdash; name, email address, phone
                number, home / property address, and unit number. Used for account
                creation, service delivery, and notifications.
              </li>
              <li data-testid="privacy-ds-location">
                <strong>Precise Location</strong> &mdash; GPS latitude/longitude
                (Staff App only), including when the app is running in the
                background. Used for property arrival verification and route
                optimization (see Section 11).
              </li>
              <li data-testid="privacy-ds-photos">
                <strong>Photos & Media</strong> &mdash; images captured or uploaded
                for violation reports, vehicle safety checks, dumpster condition,
                and service-completion proof.
              </li>
              <li data-testid="privacy-ds-device-ids">
                <strong>Device IDs & App Info</strong> &mdash; APNs / FCM push
                tokens, device model, OS version, and app version. Used solely to
                deliver push notifications and diagnose bugs.
              </li>
              <li data-testid="privacy-ds-security">
                <strong>Encryption &amp; No Third-Party Sale.</strong> All data
                listed above is encrypted in transit (TLS 1.2+) and at rest. We do
                <strong> not</strong> sell or share any of it with third parties for
                advertising or marketing.
              </li>
            </ul>
          </div>

          <h3 className="text-white font-bold mt-4 mb-2">a. Information you provide</h3>
          <ul className="list-disc pl-6 space-y-1">
            <li>Account details — name, email, phone number, role, property and unit number.</li>
            <li>Login credentials — password (stored hashed), or Google account identity via Emergent Managed Google Auth.</li>
            <li>Service requests — bulk pickup requests, complaints, violation reports, and any photos or notes you submit.</li>
            <li>Vehicle and driver records (Staff App only) — license photo, pre/post-shift safety checklists, fuel receipts.</li>
          </ul>

          <h3 className="text-white font-bold mt-6 mb-2">b. Information collected automatically</h3>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Location data (Staff App):</strong> When a driver starts a route, we collect GPS coordinates so admins and Property Managers can see live progress. Location is collected only during an active shift.</li>
            <li><strong>Pickup confirmations:</strong> QR scans and timestamps from each tenant unit.</li>
            <li><strong>Device and usage data:</strong> IP address, browser type, app version, and basic page-view analytics.</li>
          </ul>

          <h3 className="text-white font-bold mt-6 mb-2">c. Photos and AI processing</h3>
          <p>
            Photos you upload (violation reports, vehicle safety checks, dumpster condition) may be analyzed by an AI vision model (Google Gemini, via Emergent's secure platform integration) to auto-suggest a violation category or condition tag. We do not use your photos to train any third-party model.
          </p>
        </Section>

        <Section title="3. How We Use Your Information">
          <ul className="list-disc pl-6 space-y-1">
            <li>Provide and operate the Services (schedule pickups, route drivers, send notifications).</li>
            <li>Send service updates by SMS (via Twilio) and email (via Resend) — e.g., "Truck arriving in 10 min", "Service complete", "Bulk pickup confirmed".</li>
            <li>Quality assurance, fraud prevention, and resolving disputes.</li>
            <li>Comply with legal obligations.</li>
          </ul>
        </Section>

        <Section title="4. Sharing Your Information">
          <p>We do not sell your personal information. We share data only with:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Property Managers</strong> — limited to residents of the property they manage (pickup status, violation reports tied to their property).</li>
            <li><strong>Service providers</strong> — Twilio (SMS), Resend (email), Google (Auth + AI Vision via Emergent), MongoDB hosting, all under contractual confidentiality obligations.</li>
            <li><strong>Legal authorities</strong> — when required by subpoena, court order, or applicable law.</li>
          </ul>
        </Section>

        <div id="photo-processing" data-testid="privacy-ai-photo-processing">
          <Section title="4A. Optional AI Bag Screening & Staff Review">
            <p>Daily trash check-in requires one photo of all trash and recycling bags. You choose either automated screening or review by assigned NECS staff. Choosing staff review does not send your photo to an AI provider, and your check-in remains pending until staff review is complete.</p>
            <p>If you select “Allow Google Gemini screening” and then “Check photo,” the selected photo is resized and sent to Google Gemini through Emergent’s AI service to identify visible bag counts and colors. We remove image metadata from the image sent for this check and do not include your account name, email, notes or address in the AI request. The image itself may still contain personal information; avoid faces, addresses and documents.</p>
            <p>Automated observations may be inaccurate. Unclear, excessive or mismatched bags prompt a retake or staff review rather than automatic approval. No fine or payment is imposed by the AI result. We keep an assessment identifier, image fingerprint, observations and your consent choice with operational records for verification and audit purposes. We do not use this feature to identify people.</p>
            <p>You can decline AI screening, select staff review instead, or withdraw permission for future checks at each submission. Permission does not authorize advertising or unrelated profiling. For removal of previously submitted photos or account data, use the account-deletion options below or contact support@nobleempirecs.com. Processing by Google and Emergent is also subject to the applicable service-provider terms.</p>
          </Section>
        </div>

        <Section title="5. Data Retention">
          <p>
            We retain account data while your account is active. Service records (pickups, violations, photos) are retained for up to 24 months for compliance and dispute resolution. Vehicle safety reports are retained for 36 months as part of fleet compliance. Inactive accounts may be deleted after 18 months of inactivity.
          </p>
        </Section>

        <Section title="6. Your Rights">
          <ul className="list-disc pl-6 space-y-1">
            <li>Request a copy of the personal data we hold about you.</li>
            <li>Request correction or deletion of your data.</li>
            <li>Opt out of marketing SMS or emails (transactional service notices will continue).</li>
            <li>Disable location tracking by revoking the permission in your device settings (note: this will prevent the Staff App from functioning).</li>
          </ul>
          <p className="mt-3">To exercise any of these rights, email <a className="text-[#C9A962] hover:underline" href="mailto:privacy@nobleempirecs.com">privacy@nobleempirecs.com</a>.</p>
        </Section>

        <Section title="7. Children">
          <p>The Services are not directed to children under 13. We do not knowingly collect personal information from anyone under 13.</p>
        </Section>

        <Section title="8. Security">
          <p>
            We use industry-standard safeguards — TLS in transit, hashed passwords, role-based access controls, and least-privilege admin tooling — to protect your information. No system is 100% secure; we encourage you to use a strong unique password.
          </p>
        </Section>

        <Section title="8A. Passkeys, Face ID &amp; Fingerprint Sign-In" data-testid="privacy-biometrics-section">
          <p>
            You may optionally enable a passkey to sign in with Face ID, Touch ID, fingerprint or your device PIN.
            Passkeys use the WebAuthn standard: your biometric data <strong>never leaves your device</strong> and is
            never sent to or stored by NECS. We store only a public key, a credential identifier and the date it was
            created. Passkeys are optional, password sign-in always remains available, and you can remove every
            passkey at any time from <em>Profile → Security</em>.
          </p>
        </Section>

        <Section title="9. SMS / Text Messaging">
          <p>
            Noble Empire Cleaning Services sends text messages (SMS) via Twilio for
            service alerts, verification codes, and — when authorized by an Administrator
            — emergency blackout notices. Your explicit consent is captured when you add
            or verify a phone number in your profile.
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li data-testid="sms-privacy-no-share">
              <strong>We will not share or sell your mobile phone number or SMS opt-in
              information with any third parties for their marketing purposes.</strong>
            </li>
            <li data-testid="sms-privacy-frequency">
              <strong>Message frequency varies</strong> based on your service activity
              and notification preferences.
            </li>
            <li data-testid="sms-privacy-rates">
              <strong>Message and data rates may apply.</strong>
            </li>
            <li data-testid="sms-privacy-stop-help">
              <strong>Reply STOP to cancel SMS notifications at any time. Reply HELP for
              assistance.</strong>
            </li>
          </ul>
          <p>
            You can also change your notification channel at any time in{' '}
            <em>Profile → Notification Preferences</em> (Email is the default; SMS is
            opt-in).
          </p>
        </Section>

        <Section title="10. Mobile Information Sharing (SMS)">
          <p data-testid="sms-mobile-info-clause">
            Mobile information will not be shared with third parties/affiliates for
            marketing/promotional purposes. All the above categories exclude text
            messaging originator opt-in data and consent; this information will not
            be shared with any third parties.
          </p>
        </Section>

        <Section title="11. Precise GPS Location (Staff App)" data-testid="privacy-gps-section">
          <p data-testid="privacy-gps-precise">
            The NECS Staff App collects <strong>Precise GPS Location</strong> data
            (latitude / longitude accurate to within a few meters) from field staff
            devices — drivers, supervisors, crew leaders, and site agents — so we can
            deliver the core service. Specifically we use precise location to:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Route optimization</strong> — sequence stops by proximity so crews minimize drive time and fuel between properties.</li>
            <li><strong>Property arrival verification</strong> — geo-fence check-ins confirm that a staff member is physically on-site before starting a job (prevents ghost clock-ins).</li>
            <li><strong>Service reporting</strong> — every pickup, violation photo, and job-completion event is tagged with its GPS coordinates so admins and Property Managers can audit the field record.</li>
            <li><strong>Live progress visibility</strong> — admins see real-time truck locations on the Ops dashboard so they can respond to tenant "where is my crew?" questions.</li>
          </ul>
          <p data-testid="privacy-gps-background">
            <strong>Background location.</strong> On both iOS and Android, the Staff
            App requests permission to access location <em>while the app is in the
            background</em>. This is required because crews often lock their phone in
            a truck mount or pocket between stops. Without background location, live
            truck tracking and proximity-based routing cannot function. iOS users are
            shown Apple's "Always Allow" prompt only after first using the app in the
            foreground; Android users can grant "Allow all the time" from the system
            permission dialog. You can revoke this permission at any time in your
            device settings — doing so will disable the Staff App's core features but
            will not affect your account or the Resident App.
          </p>
          <p>
            The Resident App does <strong>not</strong> collect precise or background
            location. Residents only need to open the app to see pickup notifications.
          </p>
          <p>
            Location data is retained for 90 days for operational auditing, then
            anonymized. It is never sold, never shared with advertisers, and never
            used for any purpose outside the service functions listed above.
          </p>
        </Section>

        <Section title="12. Account Deletion & Data Removal" data-testid="privacy-delete-section">
          <p data-testid="privacy-delete-self-service">
            You have the right to delete your NECS account at any time. There are two
            ways to do this:
          </p>
          <p><a href="/delete-account" className="text-[#C9A962] underline" data-testid="privacy-deletion-web-link">Request account deletion on the web</a> — available without reinstalling either NECS app.</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>
              <strong>In-app self-service.</strong> Sign in to the Resident or Staff
              App, open <em>Profile → Delete My Account</em>, and confirm the two-step
              prompt. Your account will be deactivated immediately and permanently
              purged within 30 days.
            </li>
            <li>
              <strong>By email.</strong> Send a request to
              {' '}<a className="text-[#C9A962] hover:underline" href="mailto:support@nobleempirecs.com">support@nobleempirecs.com</a>
              {' '}from the email address on file. Our team will verify your identity
              and complete the deletion within 30 days.
            </li>
          </ul>
          <p>
            Certain records that we are required by law or contract to retain — such
            as employment, timekeeping, payroll, billing invoices, service evidence, safety incident reports, and legal holds — will be
            retained for the minimum period mandated (typically 3-7 years) and then
            permanently destroyed. Deletion also removes any active SMS opt-in.
          </p>
        </Section>

        <Section title="13. Data Sharing — Reconfirmation" data-testid="privacy-no-sale-section">
          <p data-testid="privacy-no-sale-clause">
            <strong>We do not sell your personal data.</strong> We do not share your
            personal data — including your phone number, location, email, name, or
            any account data — with third parties for marketing or promotional
            purposes. Text messaging opt-in data and consent are never shared with
            any third party under any circumstances (see Section 10). The only data
            sharing we do is described in Section 4 above (essential service
            providers under contractual confidentiality, Property Managers strictly
            for their own residents, and legal authorities when compelled by law).
          </p>
        </Section>

        <Section title="14. Changes to This Policy">
          <p>
            We may update this Policy from time to time. Material changes will be posted on this page with a new "Last updated" date and, where required, notified by email or in-app alert.
          </p>
        </Section>

        <Section title="15. Contact & Business Address">
          <p data-testid="privacy-business-address">
            <strong>Noble Empire Cleaning Services, LLC</strong><br />
            2450 Lakeside Pkwy, Ste 150<br />
            Flower Mound, TX 75022 · United States<br />
            <a className="text-[#C9A962] hover:underline" href="mailto:privacy@nobleempirecs.com">privacy@nobleempirecs.com</a>
            {' '}·{' '}
            <a className="text-[#C9A962] hover:underline" href="mailto:support@nobleempirecs.com">support@nobleempirecs.com</a>
            {' '}·{' '}
            <a className="text-[#C9A962] hover:underline" href="tel:+18882413077">+1 (888) 241-3077</a>
          </p>
        </Section>
      </main>
    </div>
  );
}

function Section({ title, children, 'data-testid': testId }) {
  return (
    <section className="mb-10" data-testid={testId || `section-${title.split(' ')[0].toLowerCase().replace(/[^a-z0-9]/g, '')}`}>
      <h2 className="text-2xl font-bold text-white mb-3" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{title}</h2>
      <div className="space-y-3 text-sm leading-relaxed" style={{ color: '#d6d6d6' }}>{children}</div>
    </section>
  );
}
